<template>
  <div class="px-4">
    <div class="mt-5 flex w-full items-center gap-2">
      <h3 class="min-w-0 flex-1 truncate text-lg font-bold">
        {{ creator.name }}
      </h3>

      <div
        v-if="creator.lastSeen?.at || !secretToken"
        class="shrink-0">
        <AppLastSeenText :last-seen-at="creator.lastSeen?.at"/>

        <!-- Online/Offline -->
        <div
          v-if="!creator.lastSeen?.at"
          class="mx-1.5">
          <div
            v-if="creator.online"
            class="flex items-center whitespace-nowrap bg-[#2acf81] text-caption text-white rounded px-1 py-0.5">
            online

            <InlineSvg
              class="inline-block shrink-0 w-3.5 ml-0.5"
              :src="require('@/assets/svg/design/i_mobile.svg')"/>
          </div>

          <div
            v-else
            class="flex items-center justify-center whitespace-nowrap">
            <span class="text-caption text-red-400">
              offline
            </span>

            <span class="ml-1 flex-shrink-0 inline-block h-2 w-2 rounded-full bg-red-400"/>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="hasProfileMeta || hasProfileActions"
      class="mt-2 flex w-full flex-wrap items-center gap-x-2 gap-y-1.5">
      <div
        v-if="hasProfileMeta"
        class="flex flex-wrap items-center gap-1.5">
        <RatingsBadge
          v-if="showRankingBadge"
          class="whitespace-nowrap"
          :user-id="creator.userId"
          :ranking-positions="creator.rankingPositions"/>

        <!-- Verified -->
        <button
          v-if="showVerifiedBadge"
          class="group flex h-6 shrink-0 items-center whitespace-nowrap rounded-full bg-gray-100 px-2 text-xs font-semibold hover:bg-[#6fbeef] hover:text-white"
          type="button"
          @click="$refs.verifiedModal.openModal();clientEventTrack({ typeName: 'verification__popup_shown' })">
          <InlineSvg
            :src="require('@/assets/svg/design/verified.svg')"
            class="mr-1 shrink-0 group-hover:hidden"/>

          <InlineSvg
            :src="require('@/assets/svg/design/verified-hovered.svg')"
            class="mr-1 shrink-0 hidden group-hover:inline-block"/>

          Verified

          <InlineSvg
            :src="require('@/assets/svg/design/info.svg')"
            class="ml-1 w-4 shrink-0 text-[#b9babc] group-hover:text-[#b8dff8]"/>
        </button>
      </div>

      <div
        v-if="hasProfileActions"
        class="ml-auto flex shrink-0 items-center gap-1">
        <TipsModal
          v-if="showTipsAction"
          icon-class="flex h-8 w-8 items-center justify-center rounded-md bg-gray-100 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700"
          :creator="creator"/>

        <GiftsStarter
          v-if="showGiftsAction"
          icon-class="flex h-8 w-8 items-center justify-center rounded-md bg-gray-100 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700"
          :creator="creator"
          type="grid"/>
      </div>
    </div>

    <p
      v-if="creator.about"
      class="mt-2 mb-1 text-caption text-gray-600">
      {{ creator.about }}
    </p>

    <div class="flex justify-between items-center gap-2">
      <template v-if="creator.tikTokFollowerCount || creator.instagramFollowerCount">
        <template v-if="creator.tikTokFollowerCount && creator.tikTokUsername">
          <div
            v-if="creator.tikTokFollowerCount"
            class="flex items-center mt-3 mb-4 w-full">
            <div class="flex items-center rounded-lg bg-[#f6f8fa] py-1 pl-1 pr-1.5 mr-2">
              <InlineSvg
                class="inline-block w-5 h-5 mr-0.5 text-gray-500"
                :src="require('@/assets/svg/design/tiktok2.svg')"/>

              <a
                target="_blank"
                :title="creator.tikTokUsername"
                :href="`https://tiktok.com/${getTikTokUsername()}`"
                class="text-sm text-gray-700">
                @{{ creator.tikTokUsername }}
              </a>
            </div>

            <div
              v-if="creator.tikTokFollowerCount"
              class="flex items-center  py-1 pl-1 pr-1.5 mr-2">
              <div class="flex items-center text-sm text-gray-700 font-semibold leading-4">
                <InlineSvg
                  class="inline-block mr-1 w-5 h-5 p-0.5 text-gray-500"
                  :src="require('@/assets/svg/design/user.svg')"/>

                {{ countNumberFormat(creator.tikTokFollowerCount) }}
              </div>
            </div>
          </div>
        </template>

        <template v-else-if="creator.instagramUsername && creator.instagramFollowerCount">
          <div
            v-if="creator.instagramFollowerCount"
            class="flex items-center mt-3 mb-4 w-full">
            <div class="flex items-center rounded-lg bg-[#f6f8fa] py-1 pl-1 pr-1.5 mr-2">
              <InlineSvg
                class="inline-block w-4 ml-0.5 mr-1  text-gray-500"
                :src="require('@/assets/svg/design/instagram.svg')"/>

              <a
                target="_blank"
                :title="creator.instagramUsername"
                :href="`https://www.instagram.com/${creator.instagramUsername}/`"
                class="text-sm text-gray-700">
                {{ creator.instagramUsername }}
              </a>
            </div>

            <div
              v-if="creator.instagramFollowerCount"
              class="flex items-center  py-1 pl-1 pr-1.5 mr-2">
              <div class="flex items-center text-sm text-gray-700 font-semibold leading-4">
                <InlineSvg
                  class="inline-block mr-1 w-5 h-5 p-0.5 text-gray-500"
                  :src="require('@/assets/svg/design/user.svg')"/>

                {{ countNumberFormat(creator.instagramFollowerCount) }}
              </div>
            </div>
          </div>
        </template>
      </template>

      <div
        v-else
        class="flex items-center mt-3 mb-4 w-full">
        <div class="flex items-center rounded-lg bg-[#f6f8fa] p-2 mr-2">
          <span class="text-sm font-semibold mr-1 leading-4">
            {{ countNumberFormat(creator.postViewCount) }}
          </span>

          <span class="text-caption text-gray-400">
            {{ $t('creator.profile.views', creator.postViewCount) }}
          </span>
        </div>

        <div class="flex items-center rounded-lg bg-[#f6f8fa] p-2">
          <span class="text-sm font-semibold mr-1 leading-4">
            {{ countNumberFormat(creator.postLikeCount) }}
          </span>

          <span class="text-caption text-gray-400">
            {{ $t('creator.profile.likes', creator.postLikeCount) }}
          </span>
        </div>

        <template v-if="creator.tikTokUsername">
          <div class="p-2 mr-2 overflow-hidden">
            <div
              v-if="creator.tikTokUsername"
              class="block truncate">
              <InlineSvg
                class="inline-block w-5 h-5 mr-0.5"
                :src="require('@/assets/svg/design/tiktok2.svg')"/>

              <a
                target="_blank"
                :title="creator.tikTokUsername"
                :href="`https://tiktok.com/${getTikTokUsername()}`"
                class="text-sm text-gray-700">
                @{{ creator.tikTokUsername }}
              </a>
            </div>
          </div>
        </template>

        <template v-else-if="creator.instagramUsername">
          <div class="p-2 mr-2 overflow-hidden">
            <div
              v-if="creator.instagramUsername"
              class="block truncate">
              <InlineSvg
                class="inline-block w-4 h-4 mr-0.5"
                :src="require('@/assets/svg/design/instagram.svg')"/>

              <a
                target="_blank"
                :title="creator.instagramUsername"
                :href="`https://www.instagram.com/${creator.instagramUsername}/`"
                class="text-sm text-gray-700">
                {{ creator.instagramUsername }}
              </a>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div
      :class="{
        'mb-4': !creator.contact && creator.online
      }"
      class="flex w-full mt-4 space-x-2">
      <template v-if="isAuthenticated">
        <div class="w-full flex justify-between items-center">
          <div class="flex-grow w-full flex flex-wrap items-center gap-2">
            <button
              v-if="creator.blocked"
              class="flex items-center btn py-2 !px-3 btn-blue-ghost whitespace-nowrap cursor-pointer flex-grow relative stroke-[#4756DA] hover:stroke-white"
              @click="openUnblockModal(); messageMeClicked(creator.userId, 'profile')">
              {{ $t('chat.message.message_me') }}

              <InlineSvg
                v-if="$i18n.locale === 'en'"
                class="inline-block w-4 h-4 text-transparent ml-1.5"
                :src="require('@/assets/svg/design/send.svg')"/>

              <span
                v-if="creator.unreadIncomingMessageCount || showBadgeAfterSubscription"
                class="inline-flex leading-[1.2rem] items-center px-2 py-0.5 rounded-full text-xs font-mono font-medium bg-red-400 text-white absolute -top-2 -right-2">
                {{ creator.unreadIncomingMessageCount ? creator.unreadIncomingMessageCount : '1' }}
              </span>
            </button>

            <router-link
              v-else-if="creator.subscribed || !authOptions.chatRequiresSubscription"
              class="flex items-center btn py-2 !px-3 btn-blue-ghost whitespace-nowrap cursor-pointer flex-grow relative stroke-[#4756DA] hover:stroke-white"
              :to="`/chat/${creator.userId}`"
              @click="messageMeClicked(creator.userId, 'profile')">
              <template v-if="options.prelandingId === 'apreland'">
                Chat with AI assistant
              </template>

              <template v-else>
                {{ $t('chat.message.message_me') }}
              </template>

              <InlineSvg
                v-if="$i18n.locale === 'en'"
                class="inline-block w-4 h-4 text-transparent ml-1.5"
                :src="require('@/assets/svg/design/send.svg')"/>

              <span
                v-if="creator.unreadIncomingMessageCount || showBadgeAfterSubscription"
                class="inline-flex leading-[1.2rem] items-center px-2 py-0.5 rounded-full text-xs font-mono font-medium bg-red-400 text-white absolute -top-2 -right-2">
                {{ creator.unreadIncomingMessageCount ? creator.unreadIncomingMessageCount : '1' }}
              </span>
            </router-link>

            <button
              v-else-if="!secretToken"
              class="relative flex items-center btn py-2 !px-3 btn-blue-ghost whitespace-nowrap w-full stroke-[#4756DA] hover:stroke-white"
              @click="$refs.subscribeModal.openModal(); messageMeClicked(creator.userId, 'profile')">
              <template v-if="options.prelandingId === 'apreland'">
                Chat with AI assistant
              </template>

              <template v-else>
                {{ $t('chat.message.message_me') }}
              </template>

              <InlineSvg
                v-if="$i18n.locale === 'en'"
                class="inline-block w-4 h-4 text-transparent ml-1.5"
                :src="require('@/assets/svg/design/send.svg')"/>

              <span
                v-if="creator.unreadIncomingMessageCount"
                class="inline-flex leading-[1.2rem] items-center px-2 py-0.5 rounded-full text-xs font-mono font-medium bg-red-400 text-white absolute -top-2 -right-2">
                {{ creator.unreadIncomingMessageCount }}
              </span>
            </button>

            <CretorInfoMoreContentButton 
              v-if="options.moreContentEnabled"
              :user-id="creator.userId"/>
          </div>

          <div
            v-if="!secretToken && (options.tipsEnabled || options.gifts)"
            class="flex items-center gap-2 ml-4">
            <TipsModal
              v-if="options.tipsEnabled && !secretToken && creator?.username && !options.moreContentEnabled"
              :creator="creator"/>

            <GiftsStarter
              v-if="options.gifts && !secretToken && !isPremiumSupportUser"
              :creator="creator"
              type="grid"/>
          </div>
        </div>
      </template>

      <template v-else-if="!secretToken">
        <button
          type="button"
          class="flex-grow btn btn-blue-ghost stroke-[#4756DA] hover:stroke-white whitespace-nowrap cursor-pointer w-full sm:w-auto"
          @click="showOnbordingOrSignUp(); pathToRedirect = `/@${creator.username}`">
          {{ $t('chat.message.message_me') }}

          <InlineSvg
            class="inline-block w-4 h-4 text-transparent ml-1.5"
            :src="require('@/assets/svg/design/lock-2.svg')"/>
        </button>
      </template>
    </div>
  </div>

  <Modal
    ref="subscribeModal"
    v-slot="{ closeModal, isVisible }"
    name="Subscribe"
    position="bottom"
    rounded-class="rounded-t-2.5xl"
    overflow-class="overflow-y-auto"
    content-class="bg-white px-6 pt-5 pb-4"
    :has-indents="false">
    <SubscribeContent
      :creator="creator"
      :is-visible="isVisible"
      :close="closeModal"
      @subscribed="subscribed"/>
  </Modal>

  <Modal
    v-slot="{ closeModal }"
    ref="verifiedModal"
    name="Verified modal"
    position="bottom"
    overflow-class="overflow-y-auto"
    content-class="bg-white"
    wrapper-class=""
    rounded-class="rounded-t-2.5xl"
    :show-close-cross="false"
    :has-indents="false">
    <InlineSvg
      :src="require('@/assets/svg/design/cross.svg')"
      class="text-gray-900 absolute top-0 right-0 p-1 w-5 h-5 mt-6 mr-5 cursor-pointer"
      @click="closeModal"/>

    <VerifiedContent
      :social-name="((creator.tikTokUsername && creator.tikTokFollowerCount) || (creator.tikTokUsername && !creator.instagramFollowerCount)) ? 'TikTok' : 'Instagram'"
      @close="closeModal"/>
  </modal>
</template>

<script>
import Modal from '@/components/Functional/Modal'
import SubscribeContent from '@/components/ModalContents/SubscribeContent'
import { mapActions, mapState, mapWritableState } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import { useCreatorStore } from '@/stores/creator'
import { useAppStore } from '@/stores/app'
import { useUserStore } from '@/stores/user'
import { useSubscriptionsStore } from '@/stores/subscriptions'
import { random } from 'lodash'
import helpers from '@/helpers'
import TipsModal from '@/components/TipsModal.vue'
import VerifiedContent from '@/components/ModalContents/VerifiedContent.vue'
import { clientEventTrack } from '@/helpers/methods'
import CretorInfoMoreContentButton from '@/components/Creator/CreatorInfo/CretorInfoMoreContentButton.vue'
import RatingsBadge from '@/components/Ranking/Common/RankingBadge.vue'
import GiftsStarter from '@/components/Gifts/GiftsStarter.vue'
import { openUnblockModal } from '@/helpers/modals.js'
import AppLastSeenText from '@/components/App/AppLastSeenText.vue'

export default {
  name: 'CreatorInfo',
  components: {
    Modal,
    SubscribeContent,
    TipsModal,
    VerifiedContent,
    CretorInfoMoreContentButton,
    RatingsBadge,
    GiftsStarter,
    AppLastSeenText
  },
  data () {
    return {
      shareData: {}
    }
  },
  computed: {
    showRankingBadge () {
      return Boolean(
        this.authOptions?.rankings &&
          Object.keys(this.creator.rankingPositions || {}).length
      )
    },
    showVerifiedBadge () {
      return Boolean(
        this.options.verificationEnabled &&
          (this.creator.tikTokFollowerCount || this.creator.instagramFollowerCount)
      )
    },
    showTipsAction () {
      return Boolean(
        this.secretToken &&
          this.options.tipsEnabled &&
          this.creator.username
      )
    },
    showGiftsAction () {
      return Boolean(
        this.secretToken &&
          this.options.gifts &&
          !this.isPremiumSupportUser
      )
    },
    hasProfileMeta () {
      return this.showRankingBadge || this.showVerifiedBadge
    },
    hasProfileActions () {
      return this.showTipsAction || this.showGiftsAction
    },
    isPremiumSupportUser () {
      return this.premiumSupportAgentUserId.toString() === this.creator.userId
    },
    ...mapState(useAppStore, [
      'isiOS',
      'secretToken'
    ]),
    ...mapState(useUserStore, [
      'userId',
      'features',
      'premiumSupportAgentUserId'
    ]),
    ...mapState(useAuthStore, [
      'isAuthenticated',
      'isTokenExist',
      'options',
      'authOptions'
    ]),
    ...mapState(useSubscriptionsStore, [
      'recentSubscriptions'
    ]),
    ...mapWritableState(useCreatorStore, [
      'creator',
      'respondsTime',
      'likes'
    ]),
    ...mapWritableState(useAuthStore, [
      'signupIsVisible',
      'pathToRedirect'
    ]),
    showBadgeAfterSubscription () {
      return this.recentSubscriptions.includes(this.creator.userId)
    }
  },
  beforeMount () {
    if (!this.respondsTime[this.creator.username]) {
      this.respondsTime[this.creator.username] = random(5, 15)
    }
  },
  mounted () {
    this.shareData = {
      title: 'Profile on One2Fan',
      text: 'Have a look at the profile on One2Fan',
      url: `https://one2fan.com/@${this.creator.username}`
    }
  },
  methods: {
    clientEventTrack: clientEventTrack,
    openUnblockModal () {
      openUnblockModal({
        userId: this.creator.userId,
        callback: () => {
          this.$router.push(`/chat/${this.creator.userId}`)
        }
      })
    },
    showLogIn () {
      helpers.showLogIn()
    },
    showOnbordingOrSignUp () {
      helpers.showOnbordingOrSignUp()
    },
    ...mapActions(useCreatorStore, [
      'userShared'
    ]),
    getTikTokUsername () {
      return this.creator.tikTokUsername.charAt(0) === '@' ? this.creator.tikTokUsername : `@${this.creator.tikTokUsername}`
    },
    async subscribed () {
      await this.$router.push(`/chat/${this.creator.userId}`)
    },
    async like () {
      if (!this.isTokenExist) {
        helpers.openSignInByEmailModal()
      }

      this.likes[this.creator.userId] = !this.likes[this.creator.userId]
    },
    async messageMeClicked (userId, clickSource) {
      const res = await this.wsp.sendRequest({
        data: {
          userId,
          clickSource
        },
        method: 'messageMe.clicked'
      })

      console.log(res)
    }
  }
}
</script>

<style scoped>
  .border-right {
    position: relative;
  }
  .border-right:after {
    content: '';
    position: absolute;
    right: 0;
    top: calc(50% - 0.5rem);
    height: 1rem;
    width: 1px;
    background: #cccccc;
  }
</style>
